import React, { useState } from "react";
import { useSignIn } from "@clerk/clerk-react";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
} from "@mui/material";
import { ReactComponent as MCPlogo } from "../static/Icons/mcp-logo.svg";

const ForgotPassword = () => {
  const { signIn, isLoaded } = useSignIn();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleSendResetCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!email) {
        setError("Please enter your email address");
        setLoading(false);
        return;
      }

      await signIn.create({
        strategy: "reset_password_email_code",
        identifier: email,
      });

      setResetCodeSent(true);
      setSuccess(true);
    } catch (err) {
      console.error("Reset code error:", err);
      setError(err?.errors?.[0]?.message || "Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!resetCode || !newPassword || !confirmPassword) {
        setError("Please fill in all fields");
        setLoading(false);
        return;
      }

      if (newPassword !== confirmPassword) {
        setError("Passwords do not match");
        setLoading(false);
        return;
      }

      if (newPassword.length < 8) {
        setError("Password must be at least 8 characters long");
        setLoading(false);
        return;
      }

      const resetAttempt = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code: resetCode,
        password: newPassword,
      });

      if (resetAttempt.status === "complete") {
        // Password reset successful
        window.location.href = "/signin?message=password_reset_success";
      } else {
        setError("Password reset failed. Please try again.");
      }
    } catch (err) {
      console.error("Password reset error:", err);
      setError(err?.errors?.[0]?.message || "Password reset failed");
    } finally {
      setLoading(false);
    }
  };

  // Return loading state if Clerk isn't loaded yet
  if (!isLoaded) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        bgcolor: "#2B2B2B",
        color: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "500px", p: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 4,
          }}
        >
          <Box
            component={MCPlogo}
            sx={{
              width: 250,
              height: 60,
            }}
          />
        </Box>
        <Box sx={{ border: "1px solid #BCBCBC", borderRadius: 2, p: 2 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: "bold", textAlign: "center" }}
          >
            Reset Password
          </Typography>

          {!resetCodeSent ? (
            <>
              <Typography
                variant="body2"
                sx={{ mb: 3, textAlign: "center", color: "#BCBCBC" }}
              >
                Enter your email address and we'll send you a code to reset your
                password.
              </Typography>

              <Box component="form" onSubmit={handleSendResetCode}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  variant="outlined"
                  sx={{
                    mb: 2.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      color: "#BCBCBC",
                      "& fieldset": {
                        borderColor: "#BCBCBC",
                      },
                      "&:hover fieldset": {
                        borderColor: "#BCBCBC",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#BCBCBC",
                      },
                    },
                  }}
                  size="small"
                />

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Reset code sent! Check your email.
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 2,
                    py: 1.5,
                    borderRadius: 9999,
                    bgcolor: "#CFFF04",
                    color: "#000",
                    boxShadow: "0px 4px 10.2px 3px #CFFF045C",
                    textTransform: "capitalize",
                    "&:hover": {
                      bgcolor: "#b0ff12",
                    },
                    fontWeight: 600,
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Send Reset Code"
                  )}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{ mb: 3, textAlign: "center", color: "#BCBCBC" }}
              >
                Enter the reset code sent to your email and your new password.
              </Typography>

              <Box component="form" onSubmit={handleResetPassword}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Reset Code
                </Typography>
                <TextField
                  fullWidth
                  name="resetCode"
                  type="text"
                  placeholder="Enter the reset code"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  required
                  variant="outlined"
                  sx={{
                    mb: 2.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      color: "#BCBCBC",
                      "& fieldset": {
                        borderColor: "#BCBCBC",
                      },
                      "&:hover fieldset": {
                        borderColor: "#BCBCBC",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#BCBCBC",
                      },
                    },
                  }}
                  size="small"
                />

                <Typography variant="body1" sx={{ mb: 1 }}>
                  New Password
                </Typography>
                <TextField
                  fullWidth
                  name="newPassword"
                  type="password"
                  placeholder="Enter your new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  variant="outlined"
                  sx={{
                    mb: 2.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      color: "#BCBCBC",
                      "& fieldset": {
                        borderColor: "#BCBCBC",
                      },
                      "&:hover fieldset": {
                        borderColor: "#BCBCBC",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#BCBCBC",
                      },
                    },
                  }}
                  size="small"
                />

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Confirm New Password
                </Typography>
                <TextField
                  fullWidth
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  variant="outlined"
                  sx={{
                    mb: 2.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      color: "#BCBCBC",
                      "& fieldset": {
                        borderColor: "#BCBCBC",
                      },
                      "&:hover fieldset": {
                        borderColor: "#BCBCBC",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#BCBCBC",
                      },
                    },
                  }}
                  size="small"
                />

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 2,
                    py: 1.5,
                    borderRadius: 9999,
                    bgcolor: "#CFFF04",
                    color: "#000",
                    boxShadow: "0px 4px 10.2px 3px #CFFF045C",
                    textTransform: "capitalize",
                    "&:hover": {
                      bgcolor: "#b0ff12",
                    },
                    fontWeight: 600,
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </Box>
            </>
          )}

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2">
              Remember your password?{" "}
              <Link
                href="/signin"
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  fontWeight: "medium",
                  color: "#CFFF04",
                }}
              >
                Back to Login
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;